import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RouterAnchor } from 'src/mtska-frontend-app-component/components/RouterAnchor';
import { useDataProvider } from 'src/mtska-frontend-data-provider';

const Overview = ({ filters = null, dataProviderName, aggregatorLabel, aggregateBy, legalEntityClickHandler = () =>{} }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [totalRow, setTotalRow] = useState(null);
    const [drawable, setDrawable] = useState(false);

    const { items: result, loadAll, loading, reloadCounter } = useDataProvider(dataProviderName);
    const lastFiltersRef = useRef();

    const getTransposedDate = (months) => {
        const today = new Date();
        today.setMonth(today.getMonth() + months);

        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}`;
    }

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: aggregatorLabel,
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => params?.row?.name !== 'total'  ? (params?.row?.name !== '-' ? legalEntityClickHandler(params) : '-') : t('Totale'),
        },
        {
            field: 'total',
            headerName: 'Totale',
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name ;

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'in_order',
            headerName: 'In fase d\'ordine',
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=in_order';

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'running',
            headerName: 'In marcia',
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=running';

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'expired',
            headerName: 'Scadute',
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=running&_related_contract_end_at='+JSON.stringify({from: getTransposedDate(-120), to: getTransposedDate(0)});

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'threeMonths',
            headerName: "Scad. 3 mesi",//t("expiring within {{months}} months", { months: 3 }),
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=running&_related_contract_end_at='+JSON.stringify({from: getTransposedDate(0), to: getTransposedDate(3)});

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'sixMonths',
            headerName: "Scad. 6 mesi",//t("expiring within {{months}} months", { months: 6 }),
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=running&_related_contract_end_at='+JSON.stringify({from: getTransposedDate(3), to: getTransposedDate(6)});

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'twelveMonths',
            headerName: "Scad. 12 mesi",// t("expiring within {{months}} months", { months: 12 }),
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=running&_related_contract_end_at='+JSON.stringify({from: getTransposedDate(6), to: getTransposedDate(12)});

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        },
        {
            field: 'closed',
            headerName: 'Archiviato',
            headerClassName: 'header-cell',
            flex: 1,
            renderCell: (params) => {
                if (params?.row?.name !== 'total' && params?.row?.name !== '-') {
                    let qs = aggregateBy + '=' + params?.row?.name + '&status=closed';

                    if (Array.isArray(filters?.items)) {
                        filters.items.forEach((filter) => {
                            if (filter.field == aggregateBy) return qs;
                            
                            if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
                                return qs += '&' + filter.field + "=" + JSON.stringify(filter.value);
                            }
                            
                            return qs += '&' + filter.field + "=" + filter.value;
                        })
                    }
                    return (
                        <RouterAnchor
                            href={'/cars/car?' + qs}
                            underline="none"
                            className="buttonized MuiButton-variantSolid input-button tiny"
                            style={{ verticalAlign: 'middle' }}
                        >
                            {params.value}
                        </RouterAnchor>
                    );
                }
                return params.value;
            }
        }
    ], [t, legalEntityClickHandler]);

    useEffect(() => {
        const stringified = JSON.stringify(filters);
        if (lastFiltersRef.current !== stringified && !loading) {
            lastFiltersRef.current = stringified;
            loadAll(filters);
        }
    }, [filters, reloadCounter, loading, loadAll]);

    // Elaborazione dei risultati con memoizzazione
    useEffect(() => {
        if (result && Array.isArray(result)) {
            if (result.length == 0) {
                result.push({"_id":"0","name":"-","total":0,"in_order":0,"running":0,"expired":0,"threeMonths":0,"sixMonths":0,"twelveMonths":0,"closed":0})
            }

            const newTotalRow = result.reduce((acc, row) => ({
                id: result.length + 1,
                name: t('total'),
                total: acc.total + (row?.total || 0),
                in_order: acc.in_order + (row?.in_order || 0),
                running: acc.running + (row?.running || 0),
                expired: acc.expired + (row?.expired || 0),
                threeMonths: acc.threeMonths + (row?.threeMonths || 0),
                sixMonths: acc.sixMonths + (row?.sixMonths || 0),
                twelveMonths: acc.twelveMonths + (row?.twelveMonths || 0),
                closed: acc.closed + (row?.closed || 0),
            }), {
                name: t('total'),
                total:0,
                in_order:0,
                running: 0,
                expired: 0,
                threeMonths: 0,
                sixMonths: 0,
                twelveMonths: 0,
                closed:0,
            });

            // Aggiorna lo stato solo se i dati sono realmente cambiati
            setRows(prevRows => {
                const newRows = result.map((row, index) => ({
                    ...row,
                    id: index
                }));

                // Confronto superficiale per evitare aggiornamenti non necessari
                if (JSON.stringify(prevRows) !== JSON.stringify(newRows)) {
                    return newRows;
                }
                return prevRows;
            });

            setTotalRow(prev => JSON.stringify(prev) === JSON.stringify(newTotalRow) ? prev : newTotalRow);
            setDrawable(true);
        }
    }, [result, t]);

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', height: 'auto', maxHeight: 420, width: '100%', marginTop: '10px' }}>
            {loading || !drawable ? (
                <LinearProgress />
            ) : (
                <>
                    {/* {JSON.stringify(filters)} */}
                    <DataGridPro
                        rows={rows}
                        columns={columns}
                        disableSelectionOnClick
                        hideFooter
                        pinnedRows={{
                            bottom: totalRow ? [totalRow] : []
                        }}
                        getRowClassName={(params) =>
                            params.row.name === 'total' ? 'total-row' : ''
                        }
                        sx={{
                            '& .header-cell': {
                                backgroundColor: '#f5f5f5',
                                fontWeight: 'bold',
                            },
                            '& .total-row': {
                                background: 'var(--DataGrid-rowBorderColor) !important',
                                fontWeight: 'bold',
                            },
                            '& .total-cell': {
                                backgroundColor: '#f5f5f5',
                                fontWeight: 'bold',
                            },
                        }}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    {
                                        field: 'running',
                                        sort: 'desc'
                                    }
                                ]
                            }
                        }}
                    />
                </>
            )}
        </Box>
    );
};

export default React.memo(Overview);